import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/button";
import type { MasterControlSyncDryRun } from "@/hooks/useMasterControl";

type DryRunSyncModalProps = {
  open: boolean;
  preview: MasterControlSyncDryRun | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  confirming: boolean;
};

export function DryRunSyncModal({ open, preview, onOpenChange, onConfirm, confirming }: DryRunSyncModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-rose-200">Preview global sync</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                Shows pending ingestion loads before dispatching a resync.
              </Dialog.Description>
            </div>
            <button
              type="button"
              className="rounded-full border border-border p-1 text-foreground/80 hover:text-primary-foreground dark:text-amber-200"
              onClick={() => onOpenChange(false)}
            >
              ×
            </button>
          </div>

          <div className="mt-4 grid gap-4 text-sm text-foreground/80 md:grid-cols-2">
            <div className="rounded-lg border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Spotify pending</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{preview?.spotify_pending ?? 0}</p>
            </div>
            <div className="rounded-lg border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Press pending</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{preview?.press_pending ?? 0}</p>
            </div>
            <div className="rounded-lg border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Bandsintown pending</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{preview?.bit_pending ?? 0}</p>
            </div>
            <div className="rounded-lg border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Venue digests</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{preview?.venues_pending ?? 0}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirming}
              onClick={() => {
                void onConfirm();
              }}
            >
              {confirming ? "Triggering…" : "Trigger resync"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default DryRunSyncModal;
